/**
 * API Error Handling
 *
 * Custom error classes and error transformation utilities
 */

import type { ApiError } from './types';
import type { AxiosError } from 'axios';

/**
 * Custom API Error class
 */
export class ApiClientError extends Error implements ApiError {
  public readonly status?: number;
  public readonly statusText?: string;
  public readonly data?: unknown;
  public readonly code?: string;
  public readonly isApiError = true as const;

  constructor(message: string, status?: number, statusText?: string, data?: unknown, code?: string) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
    this.code = code;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiClientError);
    }
  }
}

/**
 * Network Error - when request fails to reach the server
 */
export class NetworkError extends ApiClientError {
  constructor(message: string = 'Network error occurred') {
    super(message, undefined, undefined, undefined, 'NETWORK_ERROR');
    this.name = 'NetworkError';
  }
}

/**
 * Timeout Error - when request times out
 */
export class TimeoutError extends ApiClientError {
  constructor(message: string = 'Request timeout') {
    super(message, undefined, undefined, undefined, 'TIMEOUT');
    this.name = 'TimeoutError';
  }
}

/**
 * Authentication Error - 401 Unauthorized
 */
export class AuthenticationError extends ApiClientError {
  constructor(message: string = 'Authentication required') {
    super(message, 401, 'Unauthorized', undefined, 'AUTHENTICATION_ERROR');
    this.name = 'AuthenticationError';
  }
}

/**
 * Authorization Error - 403 Forbidden
 */
export class AuthorizationError extends ApiClientError {
  constructor(message: string = 'Access forbidden') {
    super(message, 403, 'Forbidden', undefined, 'AUTHORIZATION_ERROR');
    this.name = 'AuthorizationError';
  }
}

/**
 * Not Found Error - 404
 */
export class NotFoundError extends ApiClientError {
  constructor(message: string = 'Resource not found') {
    super(message, 404, 'Not Found', undefined, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

/**
 * Validation Error - 422 Unprocessable Entity
 */
export class ValidationError extends ApiClientError {
  constructor(message: string = 'Validation failed', data?: unknown) {
    super(message, 422, 'Unprocessable Entity', data, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}

/**
 * Server Error - 5xx
 */
export class ServerError extends ApiClientError {
  constructor(message: string = 'Server error occurred', status: number = 500) {
    super(message, status, 'Internal Server Error', undefined, 'SERVER_ERROR');
    this.name = 'ServerError';
  }
}

/**
 * Transform Axios error into ApiClientError
 */
export function transformError(error: AxiosError): ApiClientError {
  // Network error (no response)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return new TimeoutError(error.message);
    }
    return new NetworkError(error.message || 'Network error occurred');
  }

  const { status, statusText, data } = error.response;
  const errorData = data as Record<string, unknown> | undefined;
  const message = (errorData?.message as string) || (errorData?.error as string) || statusText || 'Request failed';

  // Map status codes to specific error types
  switch (status) {
    case 401:
      return new AuthenticationError(message);
    case 403:
      return new AuthorizationError(message);
    case 404:
      return new NotFoundError(message);
    case 422:
      return new ValidationError(message, data);
    case 400:
      return new ApiClientError(message, status, statusText, data, 'BAD_REQUEST');
    default:
      if (status >= 500) {
        return new ServerError(message, status);
      }
      return new ApiClientError(message, status, statusText, data);
  }
}

/**
 * Check if error should be retried
 */
export function shouldRetry(
  error: ApiClientError,
  retryCount: number,
  maxRetries: number
): boolean {
  // Don't retry if max retries reached
  if (retryCount >= maxRetries) {
    return false;
  }

  // Retry network errors
  if (error instanceof NetworkError || error instanceof TimeoutError) {
    return true;
  }

  // Retry server errors (5xx)
  if (error instanceof ServerError) {
    return true;
  }

  // Retry specific status codes
  if (error.status && [408, 429, 502, 503, 504].includes(error.status)) {
    return true;
  }

  // Don't retry client errors (4xx)
  return false;
}

/**
 * Calculate retry delay with exponential backoff
 */
export function getRetryDelay(retryCount: number, baseDelay: number = 1000): number {
  // Exponential backoff: baseDelay * 2^retryCount
  const delay = baseDelay * Math.pow(2, retryCount);
  // Add some jitter (±25%)
  const jitter = delay * 0.25 * (Math.random() - 0.5);
  return Math.min(delay + jitter, 30000); // Cap at 30 seconds
}
